import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Bar, BarChart, CartesianGrid, XAxis, YAxis } from 'recharts'
import { loadLeadsPerDay, type LeadsPerDay } from '@/api/overview'
import { useApiErrorMessage } from '@/api/errors'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Card } from '@/components/ui/card'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart'
import { Skeleton } from '@/components/ui/skeleton'
import { formatDate, formatDayAndMonth, formatPeriod } from '@/lib/dates'

/**
 * How many leads were written on each day of the fair.
 *
 * A chart rather than another tile, because this is the one figure on the screen that is a
 * *series*: the days of the fair share a scale and mean something in relation to each other — the
 * Saturday that brought twice what Thursday did is the whole point, and no tile can show it.
 *
 * Bars, not a line: each day is a count of its own and nothing happens between two of them. A line
 * would draw a slope through the night as though leads had trickled in while the stand was closed.
 *
 * The window is the fair's own period, decided by the backend from the fair this is given — so the
 * chart cannot end up covering days the card above it does not.
 */
export function LeadsPerDayChart({ tradeFairId }: { tradeFairId: string }) {
  const { t } = useTranslation()
  const errorMessage = useApiErrorMessage()
  // Both outcomes remember which fair they belong to, and both are read back through that below.
  // A fair that changes stops the previous answer from being an answer *at that moment*, not when
  // the next one arrives — otherwise the card would go on drawing one fair's days for a second or
  // two under another fair's name, which is worse than showing nothing.
  const [loaded, setLoaded] = useState<{ tradeFairId: string; series: LeadsPerDay } | null>(null)
  const [failed, setFailed] = useState<{ tradeFairId: string; cause: unknown } | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    loadLeadsPerDay(tradeFairId, controller.signal)
      .then((series) => !controller.signal.aborted && setLoaded({ tradeFairId, series }))
      .catch((cause: unknown) => !controller.signal.aborted && setFailed({ tradeFairId, cause }))
    return () => controller.abort()
  }, [tradeFairId])

  const series = loaded?.tradeFairId === tradeFairId ? loaded.series : null
  const error = failed?.tradeFairId === tradeFairId ? failed.cause : null

  const config = {
    leads: { label: t('dashboard.leadsPerDayLegend'), color: 'var(--chart-1)' },
  } satisfies ChartConfig

  return (
    <Card className="grid gap-4 p-6">
      <div className="grid gap-1">
        <h2 className="font-heading leading-tight font-semibold">{t('dashboard.leadsPerDay')}</h2>
        {/* The period the *answer* covers, not the one that was asked for: until the first
            response arrives there is no honest range to name. */}
        {series ? (
          <p className="text-muted-foreground text-sm">
            {t('dashboard.leadsPerDayTotal', {
              count: series.total,
              period: formatPeriod(series.from, series.to),
            })}
          </p>
        ) : (
          <Skeleton className="h-4 w-44" />
        )}
      </div>

      {/* Nothing but the error: an empty axis would read as "no leads", which is a statement about
          the business and not about a request that failed. */}
      {error ? (
        <Alert variant="destructive">
          <AlertDescription>
            {errorMessage(error, t('dashboard.leadsPerDayFailed'))}
          </AlertDescription>
        </Alert>
      ) : !series ? (
        <Skeleton className="h-56 w-full" />
      ) : series.total === 0 ? (
        // A row of zero-height bars is a chart of nothing; saying so takes one line and reads.
        <p className="text-muted-foreground flex h-56 items-center justify-center text-sm">
          {t('dashboard.leadsPerDayEmpty')}
        </p>
      ) : (
        <ChartContainer config={config} className="h-56 w-full">
          {/* The bottom margin is what the day labels sit in. Without it they end four pixels
              above the edge of the chart, and the card clips whatever a different zoom level or
              font metric pushes past it — which is how the dates end up cut in half. */}
          <BarChart data={series.days} margin={{ left: 0, right: 0, top: 4, bottom: 12 }}>
            {/* Horizontal only: the vertical lines would be one per day and outnumber the bars. */}
            <CartesianGrid vertical={false} />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              // A fair is a handful of days and every one of them fits; a month-long one drops the
              // labels that would collide rather than showing every nth, which keeps the first and
              // the last day readable either way.
              minTickGap={24}
              tickFormatter={formatDayAndMonth}
            />
            <YAxis
              tickLine={false}
              axisLine={false}
              width={28}
              // Half a lead is not a thing, and 0.5 on the axis of a quiet day is nonsense.
              allowDecimals={false}
            />
            <ChartTooltip
              content={<ChartTooltipContent labelFormatter={(label) => formatDate(String(label))} />}
            />
            {/* Square, because that is the brand's rule everywhere (`--radius: 0rem`), and capped
                in width: a three-day fair spreads four categories over the whole card, and bars
                a hundred pixels wide stop reading as a scale and start reading as blocks. */}
            <Bar dataKey="leads" fill="var(--color-leads)" maxBarSize={56} />
          </BarChart>
        </ChartContainer>
      )}
    </Card>
  )
}
